import React, { useEffect, useState } from 'react';
import { DropdownOption, capitalCase } from '@keplerco/core';
import { MappingType } from '../../../../../enums';
import { Entity } from '../../../../../models';
import { useAppActions, useAppState } from '../../../../../overmind';
import { ICourseMappingCourseConditionProps } from '../course-mapping-course.models';
import { EntitiesCardWidget } from '../../../../../components/cards/entities-card.widget';
import { LANGUAGES } from '../../../../../library/consts/languages';
import { DisplayList } from '../../../../../components/lists/display-list';
import { ListItem } from '../../../../../components/lists/list-item';
import { SearchRolesRequest } from '../../../../../models/overmind/search-params';

function getMappingTypeString(type: MappingType): string {
  switch (type) {
    case MappingType.Language: {
      return 'language';
    }

    case MappingType.Team: {
      return 'team';
    }

    case MappingType.Department: {
      return 'department';
    }

    case MappingType.Role: {
      return 'role';
    }

    default: {
      return 'entity';
    }
  }
}

export function CourseMappingCourseCondition({ type, entities, onClickAddEntity, onClickRemoveEntity }: ICourseMappingCourseConditionProps): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  function onClickEntityOption(entity: Entity) {
    setSelectedEntityOptions(currentState => {
      const nextState = structuredClone(currentState);
      const index = nextState.indexOf(entity.slug!);
      if (index === -1) {
        nextState.push(entity.slug!);
        onClickAddEntity(type, entity);
      } else {
        nextState.splice(index, 1);
        onClickRemoveEntity(type, entity.slug!);
      }
      return nextState;
    });
  }

  const [selectedEntityOptions, setSelectedEntityOptions] = useState<string[]>([]);
  const [entityOptions, setEntityOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    async function fetchEntityOptions() {
      const selectedEntities = entities?.map(entity => entity.slug!) ?? [];
      setSelectedEntityOptions(selectedEntities);

      switch (type) {
        case MappingType.Role: {

          const rolesRequest: SearchRolesRequest = {
            companySlug: companyVariables.slug,
            searchGlobally: false,
            page: 1,
            pageSize: 99999,
            search: undefined,
            sortAscending: true,
          };

          const roles = await actions.getCompanyRoles(rolesRequest);
          setEntityOptions(
            roles?.roles.map(role => ({
              value: role.slug!,
              label: role.name,
              onClick: () => onClickEntityOption({ slug: role.slug, name: role.name }),
            })) ?? []
          );
          break;
        }
        case MappingType.Department: {
          const departments = await actions.getDepartments(companyVariables.slug!);
          setEntityOptions(
            departments?.map(department => ({
              value: department.slug,
              label: department.name,
              onClick: () => onClickEntityOption({ slug: department.slug, name: department.name }),
            })) ?? []
          );
          break;
        }

        case MappingType.Team: {
          const teams = await actions.getTeams(companyVariables.slug!);
          setEntityOptions(
            teams?.map(team => ({
              value: team.slug!,
              label: team.teamName,
              onClick: () => onClickEntityOption({ slug: team.slug, name: team.teamName }),
            })) ?? []
          );
          break;
        }

        case MappingType.Language: {
          setEntityOptions(
            LANGUAGES.map(language => ({
              value: language,
              onClick: () => onClickEntityOption({ slug: language, name: language }),
            })) ?? []
          );
          break;
        }

        default: {
          break;
        }
      }
    }

    fetchEntityOptions();
  }, [type, entities, companyVariables.slug]);

  return (
    <EntitiesCardWidget title={`${capitalCase(getMappingTypeString(type))}s`}>
      <DisplayList dropdownValue={selectedEntityOptions} dropdownOptions={entityOptions}>
        {entities?.map(entity => (
          <ListItem key={entity.slug} onClick={() => onClickRemoveEntity(type, entity.slug!)}>
            <span className="body" style={{ color: 'var(--accent-2)' }}>
              {entity.name}
            </span>
          </ListItem>
        ))}
      </DisplayList>
    </EntitiesCardWidget>
  );
}
